import React from 'react';
import {createBrowserRouter, Navigate, Outlet} from 'react-router-dom';
import Dashboard from './views/Dashboard';
import LoginPage from './components/auth/LoginPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import Results from './views/Results';
import TestKits from './views/TestKits';
import RegisterKit from './views/RegisterKit';
import Support from './views/Support';
import Admin from './views/Admin';
import PatientSwitcher from './views/PatientSwitcher';
import Settings from './views/Settings';
import SingleResult from './views/SingleResult';
import Verify from './views/Verify';
import ProviderConsole from './views/ProviderConsole';
import {useAuth} from './hooks/useAuth';
import {isProviderOrOrganizationUser} from './utils/tokenUtils';
import RegisterPage from '@/components/auth/RegisterPage';

function getDefaultAuthedRoute(token?: string | null): string {
    return isProviderOrOrganizationUser(token) ? '/provider' : '/dashboard';
}

const ProtectedLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    if (!user?.isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    return <Outlet />;
};

const PatientOnlyLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    if (!user?.isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    if (isProviderOrOrganizationUser(user.token)) {
        return <Navigate to="/provider" replace />;
    }

    return <Outlet />;
};

const AuthLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    if (user?.isLoggedIn) {
        return <Navigate to={getDefaultAuthedRoute(user.token)} replace />;
    }

    return <Outlet />;
};

const DefaultRedirect: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    return <Navigate to={user?.isLoggedIn ? getDefaultAuthedRoute(user.token) : '/dashboard'} replace />;
};

const ProviderOnlyLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    if (!user?.isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    // Match the app's "page doesn't exist" behavior.
    if (!isProviderOrOrganizationUser(user.token)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
};

export const router = createBrowserRouter([
    {
        element: <AuthLayout />,
        children: [
            {
                path: '/login',
                element: <LoginPage />,
            },
            {
                path: '/register',
                element: <RegisterPage />,
            },
            {
                path: '/reset',
                element: <ResetPasswordPage />,
            },
            {
                path: '/reset/:token',
                element: <ResetPasswordPage />,
            },
        ],
    },
    {
        element: <ProtectedLayout />,
        children: [
            {
                element: <PatientOnlyLayout />,
                children: [
                    {
                        path: '/dashboard',
                        element: <Dashboard />,
                    },
                    {
                        path: '/results',
                        element: <Results />,
                    },
                    {
                        path: '/test-kits',
                        element: <TestKits />,
                    },
                    {
                        path: '/test-kits/activate',
                        element: <RegisterKit />,
                    },
                    {
                        path: '/patients',
                        element: <PatientSwitcher />,
                    },
                    {
                        path: '/settings',
                        element: <Settings />,
                    },
                ],
            },
            {
                path: '/result/:id',
                element: <SingleResult />,
            },
            {
                path: '/support',
                element: <Support />,
            },
            {
                path: '/admin',
                element: <Admin />,
            },
            {
                element: <ProviderOnlyLayout />,
                children: [
                    {
                        path: '/provider',
                        element: <ProviderConsole />,
                    },
                ],
            },
        ],
    },
    {
        path: '/verify/:token',
        element: <Verify />,
    },
    {
        path: '/',
        element: <DefaultRedirect />,
    },
    {
        path: '*',
        element: <DefaultRedirect />,
    },
]);
